import React from 'react';
import { cs } from '@bbkAdminUtils/css-utils';
import CustomerHtml from '@bbkFunnel/components/customer_html/customer_html';

export type AlignedInlineListProps = React.HTMLAttributes<HTMLDivElement> & {
  value: string;
  gutter?: string; // space from left edge where text begins
  centerImages?: boolean;
};

const AlignedInlineList: React.FC<AlignedInlineListProps> = ({
  value,
  gutter = '4px', // default gutter
  centerImages = false,
  className,
  style,
  ...rest
}) => {
  const classes = cs(
    'tw-relative tw-whitespace-pre-wrap tw-text-pricingtable-text-base',

    '[&_.slate-h1]:tw-py-2  [&_.slate-h1]:tw-font-bold [&_.slate-h1]:!tw-text-base [&_.slate-h1]:!tw-text-pricingtable-text-base',

    // paragraphs: start at gutter
    `[&_.slate-p]:tw-pl-[${gutter}]`,

    // reset list padding/margins and keep native markers
    '[&_.slate-ol]:tw-pl-0 [&_.slate-ul]:tw-pl-0',
    '[&_.slate-ol]:tw-m-0 [&_.slate-ul]:tw-m-0',
    '[&_.slate-ol]:tw-list-decimal [&_.slate-ul]:tw-list-disc',

    // list items: text starts at gutter, markers outside
    `[&_.slate-li]:tw-relative [&_.slate-li]:tw-pl-[${gutter}]`,

    // prevent double indent inside <li><div class='slate-p'>
    `[&_.slate-li_.slate-p]:tw-pl-0`,

    // bullet point support
    '[&_.slate-ul]:!tw-whitespace-normal [&_.slate-ol]:!tw-whitespace-normal',
    '[&_.slate-li]:!tw-whitespace-normal',
    '[&_.slate-li>.slate-p]:tw-inline [&_.slate-li>div]:tw-inline',

    // strikethrough support
    '[&_.slate-strikethrough]:tw-line-through',
    '[&_.slate-blockquote]:!tw-my-1.5 [&_.slate-blockquote]:!tw-rounded',
    '[&_.slate-blockquote]:!tw-border-0 [&_.slate-blockquote]:!tw-border-l-0',
    '[&_.slate-blockquote]:!tw-px-2 [&_.slate-blockquote]:!tw-py-1 [&_.slate-blockquote]:!tw-pl-2',
    '[&_.slate-blockquote]:!tw-bg-[color-mix(in_srgb,currentColor_10%,transparent)]',
    '[&_.slate-blockquote]:!tw-text-sm [&_.slate-blockquote]:!tw-not-italic',
    '[&_.slate-blockquote]:!tw-text-current',
    '[&_.slate-blockquote_.slate-p]:!tw-pl-0 [&_.slate-blockquote_p]:!tw-pl-0',
    '[&_.slate-blockquote_.slate-p]:!tw-text-current',
    '[&_.slate-blockquote_strong]:!tw-text-current',
    '[&_.slate-p]:tw-leading-5',

    // Tooltip underline/cursor: see pricing-table.scss `.slate-tooltip` (avoids relying on
    // arbitrary Tailwind variants for html-react-parser output in preview).

    '[&_a]:tw-cursor-pointer [&_a]:tw-underline [&_a:hover]:tw-text-[var(--pricingtable-style-accent-color)]',

    // constrain images to container so they do not overflow (!important overrides inline width/height)
    '[&_img]:!tw-max-w-full [&_img]:!tw-h-auto [&_img]:tw-object-contain [&_img]:tw-block',
    centerImages && '[&_img]:tw-mx-auto [&_img]:tw-my-1',

    className
  );

  return (
    <div data-rte-inline-align className={classes} style={style} {...rest}>
      <CustomerHtml
        value={value}
        allowedTags={[
          'p',
          'br',
          'ul',
          'ol',
          'li',
          's',
          'h1',
          'img',
          'b',
          'blockquote',
          'span',
        ]}
        allowedAttr={['data-tooltip', 'tabindex', 'aria-description']}
        openLinksInNewTab
      />
    </div>
  );
};

export default AlignedInlineList;
